import { baseApi, QueryTag, ApiCollectionResponse, ApiResponse } from 'shared/api';
import { Routes } from 'shared/routes';

import { IAdminUser, UserRole } from '../model/types';

interface IAdminUsersParams {
  page?: number;
  perPage?: number;
  q?: {
    email_cont?: string;
    firstName_cont?: string;
    lastName_cont?: string;
    title_cont?: string;
    location_cont?: string;
    account_id_eq?: number;
    role_in?: string[];
    first_name_or_last_name_cont?: string;
    search_field_cont?: string;
    s?: string;
  };
}
export interface IAdminCreateUserFormData {
  email: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  accountId: number;
  credits?: number;
  viewCredits?: number;
  pdfCredits?: number;
  denyRequests?: boolean;
  denyViews?: boolean;
  denyPdfs?: boolean;
  denyExports?: boolean;
}

export interface IAdminUpdateUserFormData {
  email?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  role?: UserRole;
  location?: string;
  accountId?: number;
  statusEvent?: string;
  credits?: number;
  viewCredits?: number;
  pdfCredits?: number;
  denyRequests?: boolean;
  denyViews?: boolean;
  denyPdfs?: boolean;
  denyExports?: boolean;
}

const adminUsersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    adminUsers: builder.query<ApiCollectionResponse<IAdminUser>, IAdminUsersParams>({
      query: (params) => ({
        url: Routes.backend.apiV1AdminUsersPath(),
        method: 'GET',
        params,
      }),
      providesTags: [QueryTag.AdminUsers],
    }),
    adminUser: builder.query<ApiResponse<IAdminUser>, string>({
      query: (id) => ({
        url: Routes.backend.apiV1AdminUserPath(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: QueryTag.AdminUsers, id }],
    }),
    createAdminUser: builder.mutation<ApiResponse<IAdminUser>, IAdminCreateUserFormData>({
      query: (createData) => ({
        url: Routes.backend.apiV1AdminUsersPath(),
        method: 'POST',
        data: { user: createData },
      }),
      invalidatesTags: [QueryTag.AdminUsers, QueryTag.Account],
    }),
    updateAdminUser: builder.mutation<ApiResponse<IAdminUser>, { id: number; data: IAdminUpdateUserFormData }>({
      query: ({ id, data }) => ({
        url: Routes.backend.apiV1AdminUserPath(id),
        method: 'PUT',
        data: { user: data },
      }),
      invalidatesTags: (_result, _error, { id, data }) => {
        const tags: Array<{ type: QueryTag; id?: number | string } | QueryTag> = [
          { type: QueryTag.AdminUsers, id },
          QueryTag.AdminUsers,
        ];
        // Invalidate all account caches when user status or account changes (affects seats counters)
        if (data.statusEvent || data.accountId) {
          tags.push(QueryTag.Account);
        }
        return tags;
      },
    }),
    resetAdminUserPassword: builder.mutation<void, number>({
      query: (id) => ({
        url: Routes.backend.apiV1AdminUserResetPasswordPath(id),
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useAdminUsersQuery,
  useAdminUserQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useResetAdminUserPasswordMutation,
} = adminUsersApi;
